<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  minLength,
  email as emailValidator,
} from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['cancel']);

const unusedPropShow = computed(() => props.show);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const selectedType = ref('');
const email = ref('');
const isSubmitting = ref(false);

const rules = computed(() => ({
  email: {
    required,
    email: emailValidator,
    minLength: minLength(4),
  },
}));

const v$ = useVuelidate(rules, { email });

const sentToOtherEmailAddress = computed(
  () => selectedType.value === 'other_email_address'
);

const isFormValid = computed(() => {
  if (selectedType.value) {
    if (sentToOtherEmailAddress.value) {
      return !!email.value && !v$.value.email.$error;
    }
    return true;
  }
  return false;
});

const selectedEmailAddress = computed(() => {
  const { meta } = props.currentChat;
  switch (selectedType.value) {
    case 'contact':
      return meta.sender.email;
    case 'assignee':
      return meta.assignee.email;
    case 'other_email_address':
      return email.value;
    default:
      return '';
  }
});

const onCancel = () => {
  emit('cancel');
};

const onSubmit = async () => {
  isSubmitting.value = true;
  try {
    await store.dispatch('sendEmailTranscript', {
      email: selectedEmailAddress.value,
      conversationId: props.currentChat.id,
    });
    useAlert(t('EMAIL_TRANSCRIPT.SEND_EMAIL_SUCCESS'));
    onCancel();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 402) {
      useAlert(t('EMAIL_TRANSCRIPT.SEND_EMAIL_PAYMENT_REQUIRED'));
    } else {
      useAlert(t('EMAIL_TRANSCRIPT.SEND_EMAIL_ERROR'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  dialogRef.value?.open();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('EMAIL_TRANSCRIPT.TITLE')"
    :description="t('EMAIL_TRANSCRIPT.DESC')"
    :confirm-button-label="t('EMAIL_TRANSCRIPT.SUBMIT')"
    :cancel-button-label="t('EMAIL_TRANSCRIPT.CANCEL')"
    :disable-confirm-button="!isFormValid"
    :is-loading="isSubmitting"
    @confirm="onSubmit"
    @close="onCancel"
  >
    <span v-if="unusedPropShow" class="hidden" />
    <div class="flex flex-col w-full gap-3 py-1">
      <div
        v-if="currentChat.meta.sender && currentChat.meta.sender.email"
        class="flex items-center gap-2"
      >
        <input
          id="contact"
          v-model="selectedType"
          type="radio"
          name="selectedType"
          value="contact"
          class="accent-primary"
        />
        <label
          for="contact"
          class="text-sm font-medium text-n-slate-12 cursor-pointer select-none"
        >
          {{ t('EMAIL_TRANSCRIPT.FORM.SEND_TO_CONTACT') }}
        </label>
      </div>
      <div v-if="currentChat.meta.assignee" class="flex items-center gap-2">
        <input
          id="assignee"
          v-model="selectedType"
          type="radio"
          name="selectedType"
          value="assignee"
          class="accent-primary"
        />
        <label
          for="assignee"
          class="text-sm font-medium text-n-slate-12 cursor-pointer select-none"
        >
          {{ t('EMAIL_TRANSCRIPT.FORM.SEND_TO_AGENT') }}
        </label>
      </div>
      <div class="flex items-center gap-2">
        <input
          id="other_email_address"
          v-model="selectedType"
          type="radio"
          name="selectedType"
          value="other_email_address"
          class="accent-primary"
        />
        <label
          for="other_email_address"
          class="text-sm font-medium text-n-slate-12 cursor-pointer select-none"
        >
          {{ t('EMAIL_TRANSCRIPT.FORM.SEND_TO_OTHER_EMAIL_ADDRESS') }}
        </label>
      </div>
      <div v-if="sentToOtherEmailAddress" class="w-full mt-2">
        <div class="flex flex-col gap-1.5">
          <input
            v-model="email"
            type="email"
            required
            :placeholder="t('EMAIL_TRANSCRIPT.FORM.EMAIL.PLACEHOLDER')"
            class="reset-base w-full h-10 py-2 px-3 text-sm border border-n-weak focus:border-n-brand rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
            :class="{
              'border-n-ruby-9 focus:border-n-ruby-9': v$.email.$error,
            }"
            @input="v$.email.$touch"
          />
          <span
            v-if="v$.email.$error"
            class="text-xs text-n-ruby-11 font-medium"
          >
            {{ t('EMAIL_TRANSCRIPT.FORM.EMAIL.ERROR') }}
          </span>
        </div>
      </div>
    </div>
  </Dialog>
</template>
